import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { LinkedInSessionStatus, OutreachAgentStatus, LeadStatus } from './mockData';

export interface OutreachCampaignAssignment {
  id: string;
  name: string;
  campaign_status: string;
  assignment_status: string;
  assigned_at: string | null;
}

export interface OutreachAgent {
  id: string;
  workspace_id: string;
  name: string;
  owner_name: string | null;
  status: OutreachAgentStatus;
  linkedin_status: LinkedInSessionStatus;
  proxy_country: string | null;
  proxy_session: string | null;
  daily_limit: number;
  daily_used: number;
  qualified_today: number;
  acceptance_rate: number;
  last_seen_at: string | null;
  uptime_label: string | null;
  service_name: string | null;
  novnc_url: string | null;
  metadata: Record<string, unknown>;
  assigned_campaigns: OutreachCampaignAssignment[];
  created_at: string | null;
  updated_at: string | null;
}

export interface OutreachLead {
  id: string;
  workspace_id: string;
  campaign_id: string | null;
  agent_id: string | null;
  full_name: string;
  role_title: string | null;
  company: string | null;
  location: string | null;
  profile_url: string | null;
  score: number;
  status: LeadStatus;
  last_event: string | null;
  agent_name: string | null;
  campaign_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CampaignOption {
  id: string;
  name: string;
  code: string | null;
  status: string;
  channelsEnabled: string[];
}

interface ListResponse<T> {
  items: T[];
  total: number;
  limit?: number;
  offset?: number;
}

interface ListOptions {
  limit?: number;
  offset?: number;
}

export interface CreateOutreachAgentInput {
  name?: string;
  owner_name?: string;
  proxy_country?: string;
  proxy_session?: string;
  daily_limit?: number;
  novnc_url?: string;
  service_name?: string;
  provision?: boolean;
}

export type UpdateOutreachAgentInput = Partial<CreateOutreachAgentInput>;

export function useOutreachAgents(workspaceId?: string | null, options: ListOptions = {}) {
  return useQuery<ListResponse<OutreachAgent>>({
    queryKey: ['outreach', 'agents', workspaceId, options.limit ?? null, options.offset ?? null],
    queryFn: async () => {
      const { data } = await apiClient.get<ListResponse<OutreachAgent>>('/v1/outreach/agents', {
        params: { workspace_id: workspaceId, limit: options.limit, offset: options.offset },
      });
      return data;
    },
    enabled: !!workspaceId,
    refetchInterval: 30_000,
  });
}

export function useOutreachAgent(agentId?: string, workspaceId?: string | null) {
  return useQuery<{ item: OutreachAgent }>({
    queryKey: ['outreach', 'agents', workspaceId, agentId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ item: OutreachAgent }>(`/v1/outreach/agents/${agentId}`, {
        params: { workspace_id: workspaceId },
      });
      return data;
    },
    enabled: !!workspaceId && !!agentId,
    refetchInterval: 30_000,
  });
}

export function useOutreachLeads(
  workspaceId?: string | null,
  filters: { agentId?: string; q?: string; status?: LeadStatus | 'all'; limit?: number; offset?: number } = {},
) {
  return useQuery<ListResponse<OutreachLead>>({
    queryKey: [
      'outreach',
      'leads',
      workspaceId,
      filters.agentId ?? null,
      filters.q ?? '',
      filters.status ?? 'all',
      filters.limit ?? null,
      filters.offset ?? null,
    ],
    queryFn: async () => {
      const { data } = await apiClient.get<ListResponse<OutreachLead>>('/v1/outreach/leads', {
        params: {
          workspace_id: workspaceId,
          agent_id: filters.agentId,
          q: filters.q?.trim() || undefined,
          status: filters.status && filters.status !== 'all' ? filters.status : undefined,
          limit: filters.limit,
          offset: filters.offset,
        },
      });
      return data;
    },
    enabled: !!workspaceId,
    refetchInterval: 30_000,
  });
}

export function useCampaignOptions(workspaceId?: string | null) {
  return useQuery<ListResponse<CampaignOption>>({
    queryKey: ['campaigns', 'options', workspaceId],
    queryFn: async () => {
      const { data } = await apiClient.get<ListResponse<CampaignOption>>('/v1/campaigns', {
        params: { workspace_id: workspaceId, limit: 200 },
      });
      return data;
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

export function useOutreachActions(workspaceId?: string | null) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['outreach'] });
    void qc.invalidateQueries({ queryKey: ['campaigns', 'options', workspaceId] });
  };

  const createAgent = useMutation({
    mutationFn: async (body: CreateOutreachAgentInput) => {
      const { data } = await apiClient.post<{ item: OutreachAgent }>('/v1/outreach/agents', {
        workspace_id: workspaceId,
        ...body,
      });
      return data.item;
    },
    onSuccess: invalidate,
  });

  const updateAgent = useMutation({
    mutationFn: async ({ agentId, body }: { agentId: string; body: UpdateOutreachAgentInput }) => {
      const { data } = await apiClient.patch<{ item: OutreachAgent }>(`/v1/outreach/agents/${agentId}`, {
        workspace_id: workspaceId,
        ...body,
      });
      return data.item;
    },
    onSuccess: invalidate,
  });

  const provisionAgent = useMutation({
    mutationFn: async (agentId: string) => {
      const { data } = await apiClient.post<{ item: OutreachAgent; provisioning?: { ok: boolean; error?: string } }>(
        `/v1/outreach/agents/${agentId}/provision`,
        {
          workspace_id: workspaceId,
        },
      );
      return data;
    },
    onSuccess: invalidate,
  });

  const syncEasypanelAgents = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<ListResponse<OutreachAgent>>('/v1/outreach/agents/sync-easypanel', {
        workspace_id: workspaceId,
      });
      return data;
    },
    onSuccess: invalidate,
  });

  const assignCampaign = useMutation({
    mutationFn: async ({ agentId, campaignId }: { agentId: string; campaignId: string }) => {
      const { data } = await apiClient.post<{ item: OutreachAgent }>(
        `/v1/outreach/agents/${agentId}/campaigns`,
        {
          workspace_id: workspaceId,
          campaign_id: campaignId,
        },
      );
      return data.item;
    },
    onSuccess: invalidate,
  });

  const unassignCampaign = useMutation({
    mutationFn: async ({ agentId, campaignId }: { agentId: string; campaignId: string }) => {
      await apiClient.delete(`/v1/outreach/agents/${agentId}/campaigns/${campaignId}`, {
        params: { workspace_id: workspaceId },
      });
    },
    onSuccess: invalidate,
  });

  const pauseAgent = useMutation({
    mutationFn: async (agentId: string) => {
      const { data } = await apiClient.post<{ item: OutreachAgent }>(`/v1/outreach/agents/${agentId}/pause`, {
        workspace_id: workspaceId,
      });
      return data.item;
    },
    onSuccess: invalidate,
  });

  const resumeAgent = useMutation({
    mutationFn: async (agentId: string) => {
      const { data } = await apiClient.post<{ item: OutreachAgent }>(`/v1/outreach/agents/${agentId}/resume`, {
        workspace_id: workspaceId,
      });
      return data.item;
    },
    onSuccess: invalidate,
  });

  return {
    createAgent,
    updateAgent,
    provisionAgent,
    syncEasypanelAgents,
    assignCampaign,
    unassignCampaign,
    pauseAgent,
    resumeAgent,
  };
}
